/**
 * Step-Up Authentication Store (v2)
 *
 * Holds a short-lived "step-up" proof token used to authorize sensitive
 * actions (account changes, team funding, etc.) without forcing a full
 * re-login. The proof is VOLATILE: it lives only in reactive refs and is
 * NEVER persisted to localStorage — a page reload must re-mint it.
 *
 * `obtainProof()` is single-flight: N concurrent callers from a cold cache
 * open exactly ONE StepUpAuth modal. StepUpAuth itself POSTs
 * `/api/v2/step_up` and emits the minted proof response; this store awaits
 * that response and caches it. All callers share the same in-flight mint and
 * resolve to the same token. See backend-step-up-auth design.md §D5.
 *
 * The store also holds the page's ONE pending emailed code
 * (passwordless-sign-in D9): the prompt sends through `requestCode()` and reads
 * `pendingCode`, so a prompt reopened while a code is pending never sends a
 * second one.
 */

import { defineStore } from 'pinia'
import { ref, computed, defineAsyncComponent, markRaw } from 'vue'
import { useModalStore } from '@/stores/modal'
import { useApiClient } from '@/services/apiClient'

// The prompt is NOT a leaf: it reads the user store, the door's copy and the
// passkey composable. `apiClient` imports this store, so a static import here
// would put that whole store graph under every API caller and close a module
// cycle through it. It loads when a modal is first opened, as the forward
// picker does in `weMessageActions`.
const StepUpAuth = markRaw(
  defineAsyncComponent(() => import('@/components/auth/StepUpAuth.vue')),
)

export const useStepUpStore = defineStore('stepUp', () => {
  // VOLATILE state — reactive refs only, never localStorage.
  const token = ref(null)
  const expiresAtMs = ref(0)

  // In-flight mint promise for single-flight de-duplication. Not part of
  // store state — a private closure variable so it is never serialized.
  let pendingMint = null

  // One-shot timer that drops the proof exactly at expiry. A Vue `computed`
  // over `Date.now()` caches and never re-evaluates on pure time passage, so
  // without this the getter would stay stale-true after the window closes —
  // the very "flipped true once and never expired" bug this store replaces
  // (design.md §D5). Auto-clearing mutates the reactive deps, so the computed
  // flips false the instant the proof lapses. Always cleared before reuse.
  let expiryTimer = null

  const hasValidProof = computed(
    () => !!token.value && Date.now() < expiresAtMs.value,
  )

  // THE ONE PENDING CODE. `null` until a prompt asks for a code, then
  // `{ sentAt, resendAfter }` (epoch ms, seconds). Every mounted prompt shows
  // its code field while this is set or a send is in flight. It outlives
  // `clear()` and a cancelled prompt, so the next prompt reuses a code already
  // sent; caching a proof drops it.
  const pendingCode = ref(null)
  const sendingCode = ref(false)
  // The send in flight (single-flight), and a generation that a proof cached
  // mid-send bumps, so the late answer does not revive a code nobody needs.
  let codeRequest = null
  let codeGeneration = 0

  // "Send a new code" opens at `resendAvailableAt` (epoch ms). A computed over
  // `Date.now()` never re-evaluates on time alone, so a timer moves `now`.
  const resendAvailableAt = ref(0)
  const now = ref(Date.now())
  let countdownTimer = null
  const resendSecondsLeft = computed(() =>
    Math.max(0, Math.ceil((resendAvailableAt.value - now.value) / 1000)),
  )

  // Open ONE StepUpAuth modal and resolve with the minted proof response the
  // user's submission yields, or null if they cancel / dismiss it. ModalVue
  // renders the component via <component :is> and forwards `props`, so the
  // StepUpAuth `authenticated` / `cancel` emits arrive as the onAuthenticated /
  // onCancel callbacks below. StepUpAuth POSTs `/api/v2/step_up` itself and
  // emits the `{ stepUpToken, expiresIn }` response — the store no longer
  // issues the POST.
  //
  // The prompt INTERRUPTS whatever modal is open (my-account-redesign D9): a
  // cap raised inside the unlock flow's confirmation returns to it, callbacks
  // intact, on confirm or cancel. The modal store settles the prompt as a
  // cancel when it is closed from the chrome (X / Escape / backdrop) or when
  // anyone replaces it, so `pendingMint` never waits on a prompt nobody sees.
  function promptForProof() {
    const modalStore = useModalStore()
    const prompt = {
      size: 'base',
      theme: 'light',
      component: StepUpAuth,
      props: {
        inputId: 'step-up',
        cancellable: true,
        onAuthenticated: (resp) => modalStore.settleInterrupt(prompt, resp ?? null),
        onCancel: () => modalStore.settleInterrupt(prompt, null),
      },
    }
    return modalStore.interrupt(prompt)
  }

  // Cache an already-minted proof response ({ stepUpToken, expiresIn }) and arm
  // the one-shot auto-expiry timer. TWO paths cache through here:
  //   • mint() — the modal / baseFetch single-flight handshake.
  //   • the sign-in door (`signInDoor`'s `cacheHeldProof`), which hands over the
  //     proof a registration minted, so a new member's first gated writes need
  //     no prompt.
  // Returns the cached token, or null if the response is missing/malformed.
  // A cached proof ends the pending code: it was spent, or a passkey made it
  // moot, so the next prompt starts from its choice again.
  function cacheProof(resp) {
    if (!resp?.stepUpToken) return null

    token.value = resp.stepUpToken
    expiresAtMs.value = Date.now() + resp.expiresIn * 1000
    if (expiryTimer) clearTimeout(expiryTimer)
    expiryTimer = setTimeout(clear, resp.expiresIn * 1000)
    dropPendingCode()
    return token.value
  }

  function tick() {
    clearTimeout(countdownTimer)
    countdownTimer = null
    now.value = Date.now()
    const remaining = resendAvailableAt.value - now.value
    if (remaining > 0) countdownTimer = setTimeout(tick, remaining % 1000 || 1000)
  }

  function startCountdown(seconds) {
    resendAvailableAt.value = Date.now() + seconds * 1000
    tick()
  }

  function dropPendingCode() {
    codeGeneration += 1
    pendingCode.value = null
    resendAvailableAt.value = 0
    tick()
  }

  // Email the member a `step_up` code (the server sends it to their own
  // address), once per page: while a code is pending this resolves true with
  // no request, and a send in flight is shared by every caller. `resend: true`
  // sends a new code, once the countdown allows (false before then). Rejects
  // with the client's error (`status`, `code`, `details`) when the send fails;
  // each awaiting prompt shows it.
  function requestCode({ resend = false } = {}) {
    if (codeRequest) return codeRequest
    if (pendingCode.value && !resend) return Promise.resolve(true)
    if (pendingCode.value && resendSecondsLeft.value > 0) return Promise.resolve(false)

    const generation = codeGeneration
    sendingCode.value = true
    codeRequest = useApiClient()
      .post('/email_codes', { purpose: 'step_up' }, { silent403: true })
      .then((answer) => {
        if (generation !== codeGeneration) return false
        const resendAfter = Math.max(0, Number(answer?.resendAfter) || 0)
        pendingCode.value = { sentAt: Date.now(), resendAfter }
        startCountdown(resendAfter)
        return true
      })
      .finally(() => {
        sendingCode.value = false
        codeRequest = null
      })
    return codeRequest
  }

  // Run one full modal mint: prompt (StepUpAuth POSTs and emits the proof) →
  // cache. Returns the minted token, or null on cancel/failure (no token cached).
  async function mint() {
    return cacheProof(await promptForProof())
  }

  // Single-flight proof acquisition.
  //   1. Valid cached proof  → resolve it immediately (no modal, no POST).
  //   2. Mint already running → return the SAME in-flight promise, so a
  //      second gated write while the prompt is up joins it and the prompt
  //      never stacks over itself.
  //   3. Otherwise           → start one mint and share it.
  // On cancel/failure the shared promise resolves to null (NOT a rejection)
  // so each waiter's downstream retry fails independently rather than being
  // wedged on a stuck promise.
  function obtainProof() {
    if (hasValidProof.value) return Promise.resolve(token.value)
    if (pendingMint) return pendingMint

    pendingMint = (async () => {
      try {
        return await mint()
      } catch {
        return null
      } finally {
        pendingMint = null
      }
    })()
    return pendingMint
  }

  // Drops the proof only: a pending code stays for the prompt that follows.
  function clear() {
    if (expiryTimer) {
      clearTimeout(expiryTimer)
      expiryTimer = null
    }
    token.value = null
    expiresAtMs.value = 0
  }

  return {
    token,
    expiresAtMs,
    hasValidProof,
    pendingCode,
    sendingCode,
    resendSecondsLeft,
    cacheProof,
    obtainProof,
    requestCode,
    clear,
  }
})
